import { useQuery } from '@tanstack/react-query';
import type { CustomerOrderStage } from '@capsico/contracts';
import { errorStatus } from '../api/client';
import { fetchMyOrder, fetchMyOrders } from '../api/checkout';

const OPEN: CustomerOrderStage[] = [
  'awaiting_payment',
  'scheduled',
  'placed',
  'preparing',
  'ready',
  'on_the_way',
];

/** Stages live tracking does not report on (the order page keeps its own 30 s refresh). */
const PRE_LIVE: CustomerOrderStage[] = ['awaiting_payment', 'scheduled'];

/** My orders, newest first (served from cache, refreshed in the background). */
export function useMyOrders(enabled: boolean) {
  const query = useQuery({
    queryKey: ['my-orders'],
    queryFn: fetchMyOrders,
    enabled,
    staleTime: 30_000,
  });
  return {
    loading: query.isLoading,
    failed: query.isError,
    retry: () => void query.refetch(),
    rows: query.data?.rows ?? [],
  };
}

/**
 * One order. While it is still moving it refreshes every 30 s (one source, no overlapping polls:
 * react-query never starts a refetch while one is in flight); finished orders stop refreshing.
 * With live tracking on (`poll: false`) the 30 s refresh stops: the tracking socket / poll owns
 * the status and refreshes this order only when the status really changes.
 */
export function useMyOrder(orderId: string, options: { poll?: boolean } = {}) {
  const poll = options.poll ?? true;
  const query = useQuery({
    queryKey: ['my-order', orderId],
    queryFn: () => fetchMyOrder(orderId),
    enabled: orderId !== '',
    staleTime: 15_000,
    retry: (count, error) => errorStatus(error) !== 404 && count < 2,
    refetchInterval: (current) =>
      current.state.data &&
      OPEN.includes(current.state.data.stage) &&
      // Payment and scheduling are not covered by live tracking: keep refreshing those.
      (poll || PRE_LIVE.includes(current.state.data.stage))
        ? 30_000
        : false,
  });
  return {
    loading: query.isLoading,
    notFound: errorStatus(query.error) === 404,
    failed: query.isError && errorStatus(query.error) !== 404,
    retry: () => void query.refetch(),
    order: query.data ?? null,
  };
}
